class MHeader extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      headers: ["header"],
      logos: [".m-logo"],
      topbar: ".m-topbar",
      headerWrapper: ".m-header__wrapper",
      topbarClose: ".m-topbar__close",
    };

    this.domNodes = queryDomNodes(this.selectors, this);
    this.innerWidth = window.innerWidth;
    this.headerOffsetTop = this.domNodes.headerWrapper.offsetTop;
    this.headerHeight = this.domNodes.headerWrapper.offsetHeight;
    this.stickyHeader = this.dataset.sticky;

    this.classes = {
      scrollUp: "scroll-up",
      scrollDown: "scroll-down",
      stuck: "stuck",
      always: "header-sticky-always",
      splitSticky: "split-sticky-header",
      hideMenuBar: "hide-menu-bar",
      headerScrollUp: "header-scroll-up",
      headerScrollDown: "header-scroll-down",
    };

    // Scroll state — single source of truth
    this._scroll = {
      lastY: 0,
      direction: null,       // 'up' | 'down' | null
      directionLockY: null,  // where the current confirmed direction started
      rafId: null,
    };

    this.init();
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  init() {
    this.transparentHeader =
      this.domNodes?.headers?.[0]?.dataset?.transparent === "true";

    this.initAddon();
    this.initOrderTracking();
    this.updateStickyMetrics();
    this._bindResize();
    this._bindMobileEvents();
    this._initSticky();

    this.siteNav = new SiteNav(this);
    window.__sfHeader = this;
  }

  initAddon() {
    this.megamenu = new Megamenu(this);
    if (Shopify.designMode) {
      MinimogTheme = MinimogTheme || {};
      MinimogTheme?.Wishlist?.updateWishlistCount();
    }
  }

  initOrderTracking() {
    const wrappers = this.querySelectorAll("[data-order-tracking]");
    if (!wrappers.length) return;

    wrappers.forEach((wrapper) => {
      const toggle = wrapper.querySelector("[data-order-tracking-toggle]");
      const modal = wrapper.querySelector("[data-order-tracking-modal]");
      const form = wrapper.querySelector("[data-order-tracking-form]");
      const input = wrapper.querySelector("[data-order-tracking-input]");

      if (!toggle || !modal || !form || !input) return;

      const close = () => {
        wrapper.classList.remove("is-open");
        toggle.setAttribute("aria-expanded", "false");
        modal.setAttribute("aria-hidden", "true");
      };

      const open = () => {
        wrappers.forEach((item) => {
          if (item === wrapper) return;

          item.classList.remove("is-open");
          item.querySelector("[data-order-tracking-toggle]")?.setAttribute("aria-expanded", "false");
          item.querySelector("[data-order-tracking-modal]")?.setAttribute("aria-hidden", "true");
        });

        wrapper.classList.add("is-open");
        toggle.setAttribute("aria-expanded", "true");
        modal.setAttribute("aria-hidden", "false");
        window.requestAnimationFrame(() => input.focus());
      };

      toggle.addEventListener("click", (event) => {
        event.preventDefault();

        if (wrapper.classList.contains("is-open")) {
          close();
          return;
        }

        open();
      });

      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const trackingCode = input.value.trim();

        if (!trackingCode) {
          input.focus();
          return;
        }

        window.location.assign(`https://app.melhorrastreio.com.br/app/${encodeURIComponent(trackingCode)}`);
      });

      document.addEventListener("click", (event) => {
        if (!wrapper.contains(event.target)) {
          close();
        }
      });

      wrapper.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          close();
          toggle.focus();
        }
      });
    });
  }

  // ─── Metrics ──────────────────────────────────────────────────────────────

   updateStickyMetrics() {
    this.headerHeight = this.domNodes.headerWrapper.offsetHeight;

    const desktopTop = this.querySelector(".m-header__desktop .m-header__top");
    const mainMenuBar = this.querySelector(".m-header__desktop .m-header__main");

    this.desktopTopHeight = desktopTop?.offsetHeight ?? 0;

    if (desktopTop) {
      this.style.setProperty("--m-header-top-height", `${desktopTop.offsetHeight}px`);
    } else {
      this.style.removeProperty("--m-header-top-height");
    }

    if (mainMenuBar) {
      this.style.setProperty("--m-main-menu-height", `${mainMenuBar.scrollHeight}px`);
    } else {
      this.style.removeProperty("--m-main-menu-height");
    }

    // Recalculate derived values
    this._extraSpace = this._computeExtraSpace();
    this._isSplitSticky = this._computeSplitSticky();
  }

  _computeExtraSpace() {
    let space = 20;
    document.querySelectorAll(".shopify-section-group-header-group").forEach((el) => {
      if (
        !el.classList.contains("m-section-header") &&
        !el.classList.contains("m-section-scaling-logo")
      ) {
        space += el.offsetHeight;
      }
    });
    const topBar = document.querySelector(".m-topbar");
    if (topBar) space += topBar.offsetHeight;
    return space;
  }

  _computeSplitSticky() {
    return (
      this.stickyHeader === "always" &&
      this.innerWidth >= 1024 &&
      !!this.querySelector(".m-header__desktop .m-header__top") &&
      !!this.querySelector(".m-header__desktop .m-header__main")
    );
  }

  // ─── Event bindings ───────────────────────────────────────────────────────

  _bindResize() {
    let resizeRaf = null;
    window.addEventListener("resize", () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        this.innerWidth = window.innerWidth;
        this.updateStickyMetrics();
      });
    });
  }

  _bindMobileEvents() {
    // Re-init sticky state on mobile breakpoint changes
    document.addEventListener("matchMobile", () => this._resetStickyState());
    document.addEventListener("unmatchMobile", () => this._resetStickyState());
  }

  // ─── Sticky logic ─────────────────────────────────────────────────────────

  _initSticky() {
    this._scroll.lastY = window.scrollY;
    this._lastScrollHeight = document.documentElement.scrollHeight;

    window.addEventListener("scroll", this._onScroll.bind(this), { passive: true });
  }

  _onScroll() {
    // One rAF per paint frame — discard intermediate scroll events
    if (this._scroll.rafId) return;
    this._scroll.rafId = requestAnimationFrame(() => {
      this._scroll.rafId = null;
      this._processScroll();
    });
  }

  _processScroll() {
    const currentScrollHeight = document.documentElement.scrollHeight;
    const currentY = window.scrollY;
    const { lastY } = this._scroll;
    const delta = currentY - lastY;

    if (delta === 0) return;

    // Layout reflow detected — document height changed between frames.
    // The browser may have shifted scrollY to compensate (e.g. lazy images,
    // scroll-trigger animations at section boundaries). Treat as neutral:
    // absorb the new position without interpreting it as a user scroll direction.
    if (currentScrollHeight !== this._lastScrollHeight) {
      this._lastScrollHeight = currentScrollHeight;
      this._scroll.lastY = currentY;
      this._scroll.directionLockY = null;
      return;
    }

    // At or above the trigger zone — full reset
    if (currentY <= this._extraSpace) {
      this._resetStickyState();
      this._scroll.lastY = currentY;
      return;
    }

    const movingDown = delta > 0;
    const confirmed = this._confirmDirection(movingDown, currentY);

    this._scroll.lastY = currentY;

    if (!confirmed) return;

    this._applyStickyClasses(currentY);
  }

  /**
   * Direction confirmation with asymmetric hysteresis.
   * Returns true only when a direction change is confirmed past its threshold.
   * Same-direction scrolling always returns true (no threshold needed).
   */
  _confirmDirection(movingDown, currentY) {
    const THRESHOLD_HIDE = 5;   // quick to hide (feels responsive)
    // Split-sticky needs a larger threshold: hiding the menu bar triggers a
    // CSS min-height transition that can produce small compensatory scroll events.
    const THRESHOLD_SHOW = this._isSplitSticky ? 50 : 20;

    const newDir = movingDown ? "down" : "up";
    const s = this._scroll;

    // Same direction — confirm immediately, clear any pending reversal
    if (newDir === s.direction) {
      s.directionLockY = null;
      return true;
    }

    // First movement ever
    if (s.direction === null) {
      s.direction = newDir;
      s.directionLockY = null;
      return true;
    }

    // Potential reversal — start or continue measuring
    if (s.directionLockY === null) {
      s.directionLockY = this._scroll.lastY;
    }

    const threshold = newDir === "down" ? THRESHOLD_HIDE : THRESHOLD_SHOW;
    const traveled = Math.abs(currentY - s.directionLockY);

    if (traveled >= threshold) {
      s.direction = newDir;
      s.directionLockY = null;
      return true;
    }

    return false; // still within hysteresis zone
  }

  _applyStickyClasses(currentY) {
    const goingDown = this._scroll.direction === "down";
    const pastHeader = currentY > this.headerHeight + this._extraSpace;
    const pastSplit = currentY > this.desktopTopHeight + this._extraSpace;
    const pastDesktopTop = currentY > this.desktopTopHeight;

    if (this._isSplitSticky) {
      this._applySplitStickyClasses(goingDown, pastSplit, pastDesktopTop);
    } else if (this.stickyHeader === "always") {
      this._applyAlwaysStickyClasses();
    } else if (this.stickyHeader === "on_scroll_up") {
      this._applyScrollUpStickyClasses(goingDown, pastHeader);
    } else {
      this._applyDefaultStickyClasses(goingDown, pastHeader);
    }
  }

  _applySplitStickyClasses(goingDown, pastSplit, pastDesktopTop) {
    const c = this.classes;
    this.classList.add(c.always, c.splitSticky);

    if (goingDown && pastSplit) {
      this._setScrollState("down");
      this.classList.add(c.hideMenuBar);
      this.classList.remove(c.stuck);
    } else if (!goingDown && pastDesktopTop) {
      this._setScrollState("up");
      this.classList.remove(c.hideMenuBar, c.stuck);
    } else if (!pastSplit) {
      this._clearScrollState();
      this.classList.remove(c.hideMenuBar);
    }
  }

  _applyAlwaysStickyClasses() {
    const c = this.classes;
    this.classList.add(c.always);
    this.classList.remove(c.splitSticky, c.hideMenuBar);
  }

  _applyScrollUpStickyClasses(goingDown, pastHeader) {
    if (goingDown) {
      this._setScrollState("down");
      this.classList.remove(this.classes.stuck);
    } else if (pastHeader) {
      this._setScrollState("up");
      this.classList.add(this.classes.stuck);
    }
  }

  _applyDefaultStickyClasses(goingDown, pastHeader) {
    const alreadyDown = this.classList.contains(this.classes.scrollDown);

    if (goingDown && pastHeader && !alreadyDown) {
      this._setScrollState("down");
    } else if (!goingDown && alreadyDown) {
      this._setScrollState("up");
    }
  }

  // ─── Class helpers ────────────────────────────────────────────────────────

  _setScrollState(direction) {
    const c = this.classes;
    if (direction === "down") {
      this.classList.add(c.scrollDown);
      this.classList.remove(c.scrollUp);
      document.body.classList.add(c.headerScrollDown);
      document.body.classList.remove(c.headerScrollUp);
    } else {
      this.classList.add(c.scrollUp);
      this.classList.remove(c.scrollDown);
      document.body.classList.add(c.headerScrollUp);
      document.body.classList.remove(c.headerScrollDown);
    }
  }

  _clearScrollState() {
    const c = this.classes;
    this.classList.remove(c.scrollUp, c.scrollDown);
    document.body.classList.remove(c.headerScrollUp, c.headerScrollDown);
  }

  _resetStickyState() {
    const c = this.classes;
    this.classList.remove(
      c.scrollUp, c.scrollDown, c.stuck,
      c.always, c.splitSticky, c.hideMenuBar,
    );
    document.body.classList.remove(c.headerScrollUp, c.headerScrollDown);

    // Reset scroll tracking
    this._scroll.lastY = window.scrollY;
    this._scroll.direction = null;
    this._scroll.directionLockY = null;
    this._lastScrollHeight = document.documentElement.scrollHeight;
  }
}

customElements.define("m-header", MHeader);